import { atom, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { ConsultationTypeEnum } from '@/shared/enums';

export function parseConsultationType(ct: string): ConsultationTypeEnum | null {
    if (!ct) return null;
    return (
        ConsultationTypeEnum[ct as keyof typeof ConsultationTypeEnum] ?? null
    );
}

export enum QueryKeys {
    Types = 'types',
    Page = 'page',
    PatientUid = 'patientUid',
    PetId = 'petId',
    ConsultationType = 'consultationType',
    NotifyOwnDoctor = 'notifyOwnDoctor',
    ConsultationUid = 'consultationUid',
    MessageUid = 'messageUid',
    SubmissionId = 'submissionId',
    UserIdAttached = 'userIdAttached',
    AiNurse = 'aiNurse',
    SkipBookingFlow = 'skipBookingFlow',
    IsPatientPreselected = 'isPatientPreselected',
    ForceConsultationType = 'forceConsultationType',
}

export type UrlState = Partial<Record<QueryKeys, string | null>>;

// This stores the latest instance of the useSearchParams
const searchParamsAtom = atom<{
    searchParams: URLSearchParams;
    setParams: (next: URLSearchParams, replace: boolean) => void;
}>();

export function ReactRouterBridge(): React.ReactNode {
    const [searchParams, setSearchParams] = useSearchParams();
    useHydrateAtoms([
        [
            searchParamsAtom,
            {
                searchParams,
                setParams: (next: URLSearchParams, replace: boolean) =>
                    setSearchParams(next, { replace }),
            },
        ],
    ]);
    const atom = useSetAtom(searchParamsAtom);

    useEffect(() => {
        atom({
            searchParams,
            setParams: (next, replace) => setSearchParams(next, { replace }),
        });
    }, [searchParams, setSearchParams, atom]);
    return null;
}

export const urlStateAtom = atom(
    (get) => {
        const params =
            get(searchParamsAtom)?.searchParams ?? new URLSearchParams();
        return Object.fromEntries(
            Object.values(QueryKeys).map((key) => [key, params.get(key)]),
        ) as UrlState;
    },
    (get, set, state: UrlState, replace: boolean) => {
        const searchParams = get(searchParamsAtom);
        // We don't want to mutate the existing value
        const newParams = new URLSearchParams(searchParams?.searchParams);

        // overwrite / add based on current state
        Object.entries(state)
            .filter(([_, value]) => value != null)
            .forEach(([key, value]) => {
                newParams.set(key, value.toString());
            });

        searchParams?.setParams(newParams, replace);
    },
);

type UrlStateKeyUpdate =
    | string
    | null
    | {
          value: string | null;
          replace?: boolean;
      };

export const urlStateByQueryKeyAtom = atomFamily((queryKey: QueryKeys) =>
    atom(
        (get) => get(urlStateAtom)[queryKey] ?? null,
        (get, set, update: UrlStateKeyUpdate) => {
            const currentState = get(urlStateAtom);
            const value =
                typeof update === 'object' &&
                update !== null &&
                'value' in update
                    ? update.value
                    : update;
            const replace =
                typeof update === 'object' &&
                update !== null &&
                'replace' in update
                    ? (update.replace ?? true)
                    : true;

            set(
                urlStateAtom,
                {
                    ...currentState,
                    [queryKey]: value,
                },
                replace,
            );
        },
    ),
);

export const typesUrlStateAtom = urlStateByQueryKeyAtom(QueryKeys.Types);
export const pageUrlStateAtom = urlStateByQueryKeyAtom(QueryKeys.Page);
export const patientUidUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.PatientUid,
);
export const petIdUrlStateAtom = urlStateByQueryKeyAtom(QueryKeys.PetId);
export const notifyOwnDoctorUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.NotifyOwnDoctor,
);
export const consultationUidUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.ConsultationUid,
);
export const messageUidUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.MessageUid,
);
export const submissionIdUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.SubmissionId,
);
export const userIdAttachedUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.UserIdAttached,
);
export const aiNurseUrlStateAtom = urlStateByQueryKeyAtom(QueryKeys.AiNurse);
export const skipBookingFlowUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.SkipBookingFlow,
);
export const isPatientPreselectedUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.IsPatientPreselected,
);
export const forceConsultationTypeUrlStateAtom = urlStateByQueryKeyAtom(
    QueryKeys.ForceConsultationType,
);

/// This exports a typed consultationType. To get an atom with the raw string value, use `urlStateByQueryKeyAtom(QueryKey.ConsultationType)`.
export const consultationTypeUrlStateAtom = atom(
    (get) => {
        const raw = get(urlStateAtom)?.consultationType;
        if (raw == null) return null;
        // We handle unknown keys here
        return parseConsultationType(raw);
    },
    (get, set, value: ConsultationTypeEnum | null) => {
        set(
            urlStateAtom,
            {
                consultationType:
                    value == null ? null : ConsultationTypeEnum[value],
            },
            true,
        );
    },
);
